import React from 'react';
import { ShowPage } from '../../../components/crud/ShowPage';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { DescriptionList, DescriptionItem } from '../../../components/ui/description-list';
import { useShow } from '@refinedev/core';
import { Spinner } from '../../../components/ui/spinner';
import { Laptop, Wifi, WifiOff, Shield, Heart, Settings, MapPin } from 'lucide-react';

export const ConnectorDeviceShow: React.FC = () => {
  const { query } = useShow({ resource: 'devices/connectors' });
  const record = query?.data?.data;

  if (query?.isLoading || !record) return <div className="flex justify-center p-12"><Spinner /></div>;

  const healthColor = (record.health_score ?? 0) >= 90 ? 'text-success-700' : (record.health_score ?? 0) >= 70 ? 'text-warning-700' : 'text-destructive-700';

  return (
    <ShowPage>
      <div className="space-y-6">
        {/* Device Overview */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Laptop className="h-5 w-5" />Device Overview</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-start gap-8">
              <div className="text-center">
                <Laptop className="h-12 w-12 text-primary mx-auto" />
                <div className="mt-2 font-semibold text-lg">{record.name}</div>
                <div className="text-sm text-muted-foreground">{record.device_type_display}</div>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div><span className="text-sm font-medium">Serial Number</span><div className="text-sm">{record.serial_number}</div></div>
                <div><span className="text-sm font-medium">MAC Address</span><div className="text-sm font-mono">{record.mac_address}</div></div>
                <div><span className="text-sm font-medium">Status</span><div><Badge variant={record.status === 'active' ? 'success' : 'error'}>{record.status_display}</Badge></div></div>
                <div><span className="text-sm font-medium">Health Score</span>
                  <div className="w-4/5"><Progress value={record.health_score ?? 0} className="h-2 mt-1" /><span className={`text-xs font-medium ${healthColor}`}>{record.health_score ?? 0}%</span></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Location & Network */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><MapPin className="h-4 w-4" />Location & Network</CardTitle></CardHeader>
            <CardContent>
              <DescriptionList>
                <DescriptionItem label="Branch">{record.branch_name}</DescriptionItem>
                <DescriptionItem label="Location">{record.location_description}</DescriptionItem>
                <DescriptionItem label="IP Address"><span className="font-mono text-sm">{record.ip_address}</span></DescriptionItem>
                <DescriptionItem label="Port Range">{record.port_range_start} - {record.port_range_end}</DescriptionItem>
                <DescriptionItem label="VPN Enabled"><Badge variant={record.vpn_enabled ? 'success' : 'error'}>{record.vpn_enabled ? 'Yes' : 'No'}</Badge></DescriptionItem>
              </DescriptionList>
            </CardContent>
          </Card>

          {/* Security & Config */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Shield className="h-4 w-4" />Security & Configuration</CardTitle></CardHeader>
            <CardContent>
              <DescriptionList>
                <DescriptionItem label="Security Level"><Badge variant={record.security_level === 'critical' ? 'error' : record.security_level === 'high' ? 'warning' : 'info'}>{record.security_level_display}</Badge></DescriptionItem>
                <DescriptionItem label="Firmware">{record.firmware_version}</DescriptionItem>
                <DescriptionItem label="Online">{record.is_online ? <span className="flex items-center gap-1 text-success-700"><Wifi className="h-3.5 w-3.5" />Online</span> : <span className="flex items-center gap-1 text-destructive-700"><WifiOff className="h-3.5 w-3.5" />Offline</span>}</DescriptionItem>
                <DescriptionItem label="Last Heartbeat">{record.last_heartbeat ? new Date(record.last_heartbeat).toLocaleString() : 'Never'}</DescriptionItem>
              </DescriptionList>
            </CardContent>
          </Card>
        </div>

        {/* Performance Metrics */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Heart className="h-4 w-4" />Performance Metrics</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div><div className="text-xs text-muted-foreground">Uptime</div><div className="text-xl font-bold text-success-700">{record.uptime_percentage}%</div></div>
              <div><div className="text-xs text-muted-foreground">Messages Processed</div><div className="text-xl font-bold text-primary">{(record.total_messages_processed ?? 0).toLocaleString()}</div></div>
              <div><div className="text-xs text-muted-foreground">Avg Processing Time</div><div className="text-xl font-bold text-warning-700">{record.avg_processing_time_ms}ms</div></div>
              <div><div className="text-xs text-muted-foreground">Connected Analyzers</div><div className="text-xl font-bold text-info-700">{record.analyzer_count}</div></div>
            </div>
          </CardContent>
        </Card>

        {/* Deployment Info */}
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Settings className="h-4 w-4" />Deployment Information</CardTitle></CardHeader>
          <CardContent>
            <DescriptionList columns={3}>
              <DescriptionItem label="Deployment Date">{record.deployment_date ? new Date(record.deployment_date).toLocaleDateString() : 'Not set'}</DescriptionItem>
              <DescriptionItem label="Deployed By">{record.deployed_by_name || 'N/A'}</DescriptionItem>
              <DescriptionItem label="Created">{record.created_at ? new Date(record.created_at).toLocaleString() : '—'}</DescriptionItem>
              <DescriptionItem label="Updated">{record.updated_at ? new Date(record.updated_at).toLocaleString() : '—'}</DescriptionItem>
            </DescriptionList>
          </CardContent>
        </Card>
      </div>
    </ShowPage>
  );
};

export default ConnectorDeviceShow;
